import React from 'react';
import { PROJECT_LINKS } from '../projectLinks';

const processStages = [
    {
        title: 'Veri Kaynağı',
        description:
            'Önceki Yazılımcı tarafından açık GitHub deposunda yayımlanan anonim anket yanıtları kaynak olarak kullanıldı.',
    },
    {
        title: 'Veri Temizleme',
        description:
            'Ham kayıtlar Python ve pandas ile çıkarıldı; maaş, deneyim, pozisyon, kıdem ve şirket ölçeği alanları temizlendi.',
    },
    {
        title: 'Doğrulama ve Dönüştürme',
        description:
            'Gerekli alanlar doğrulandı, değerler analiz için yeniden yapılandırıldı ve maaşlar Türk lirasına normalize edildi.',
    },
    {
        title: 'Toplulaştırma',
        description:
            'Pozisyon; pozisyon ve deneyim; şirket büyüklüğü ve kıdem için üç ayrı işlenmiş CSV özeti üretildi.',
    },
    {
        title: 'Görselleştirme',
        description:
            'İşlenmiş CSV dosyaları Papa Parse ile okunarak React ve Recharts tabanlı etkileşimli arayüzde sunuldu.',
    },
] as const;

const technologies = [
    'Python',
    'pandas',
    'React',
    'Recharts',
    'Papa Parse',
] as const;

const ExternalLinkNote = () => (
    <span className="sr-only"> (yeni sekmede açılır)</span>
);

const AboutProject = () => (
    <section
        className="about-project page-shell"
        id="proje-hakkinda"
        aria-labelledby="about-project-title"
    >
        <header className="about-project-header">
            <p className="eyebrow">06 · Proje Hakkında</p>
            <h2 id="about-project-title">Ham veriden kamusal veri ürününe</h2>
            <div className="about-project-lead">
                <p>
                    Bu çalışma, açık olarak yayımlanan 5.002 anonim maaş
                    yanıtını; veri mühendisliği, analiz ve etkileşimli
                    görselleştirme adımlarıyla okunabilir bir veri ürününe
                    dönüştürüyor.
                </p>
                <p>
                    Amaç tek bir “doğru maaş” üretmek değil; pozisyon, deneyim
                    ve şirket yapısı arasındaki farkları örneklem büyüklüğüyle
                    birlikte karşılaştırılabilir kılmak.
                </p>
            </div>
        </header>

        <div
            className="project-process"
            id="verinin-izledigi-yol"
            aria-labelledby="project-process-title"
        >
            <div className="project-process-heading">
                <p className="section-kicker">Uçtan uca süreç</p>
                <h3 id="project-process-title">Verinin izlediği yol</h3>
            </div>
            <ol className="process-list">
                {processStages.map((stage, index) => (
                    <li key={stage.title}>
                        <span className="process-number" aria-hidden="true">
                            0{index + 1}
                        </span>
                        <h4>{stage.title}</h4>
                        <p>{stage.description}</p>
                    </li>
                ))}
            </ol>
        </div>

        <div className="project-detail-grid">
            <article className="aggregate-detail">
                <p className="section-kicker">Analiz yaklaşımı</p>
                <h3>Üç ayrı toplulaştırılmış veri seti</h3>
                <ul>
                    <li>Pozisyona göre medyan maaş ve örneklem</li>
                    <li>Pozisyon ve deneyime göre medyan maaş</li>
                    <li>
                        Şirket büyüklüğü ve kıdeme göre medyan maaş
                    </li>
                </ul>
                <p>
                    Maaş dağılımlarındaki uç değerlerin aritmetik ortalamayı
                    saptırabilmesi nedeniyle merkezî eğilim ölçüsü olarak
                    medyan tercih edildi.
                </p>
            </article>

            <aside
                className="technology-detail"
                aria-labelledby="technology-title"
            >
                <p className="section-kicker">Teknoloji</p>
                <h3 id="technology-title">İşleme ve sunum katmanları</h3>
                <ul aria-label="Projede kullanılan teknolojiler">
                    {technologies.map((technology) => (
                        <li key={technology}>{technology}</li>
                    ))}
                </ul>
                <p>
                    Proje; çıkarma, temizleme, doğrulama, dönüştürme, kur
                    normalizasyonu ve toplulaştırmadan tarayıcıdaki etkileşimli
                    sunuma uzanan uçtan uca bir veri mühendisliği ve
                    görselleştirme çalışmasıdır.
                </p>
            </aside>
        </div>

        <div className="project-notes-grid">
            <aside
                className="source-reference"
                aria-labelledby="data-source-title"
            >
                <p className="section-kicker">Açık veri atfı</p>
                <h3 id="data-source-title">Veri Kaynağı</h3>
                <p>
                    Bu projede kullanılan ham veriler, Önceki Yazılımcı
                    tarafından yayımlanan açık GitHub deposundan alınmıştır.
                </p>
                <a
                    className="external-link"
                    href={PROJECT_LINKS.source}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    2026 Yazılım Sektörü Maaşları — GitHub
                    <span aria-hidden="true">↗</span>
                    <ExternalLinkNote />
                </a>
                <p className="source-attribution">
                    Anket verilerinin toplanması ve ilk yayımlanması kaynak
                    depo sahibine aittir; veri temizleme, dönüştürme,
                    toplulaştırma, analiz ve bu sayfadaki görselleştirme
                    çalışmaları bu proje kapsamında gerçekleştirilmiştir.
                </p>
            </aside>

            <aside
                className="data-disclaimer"
                aria-labelledby="disclaimer-title"
            >
                <p className="section-kicker">Bağlam ve sınırlılıklar</p>
                <h3 id="disclaimer-title">Veri ve Yorumlama Notu</h3>
                <p>
                    Veri seti üçüncü tarafça kamuya açık bir depoda
                    yayımlanmıştır; özgün veriler ilgili yayıncıya ve
                    katkıda bulunanlara aittir. Bu site bağımsız bir analiz ve
                    görselleştirme projesidir; kaynak depo sahibiyle resmî
                    bağlantısı yoktur, onun tarafından desteklenmez veya
                    işletilmez.
                </p>
                <p>
                    Sonuçlar gönüllü ve anonim anket yanıtlarının
                    toplulaştırılmış analizidir ve Türkiye’deki tüm yazılım
                    sektörünü eksiksiz temsil etmeyebilir. Örneklem
                    büyüklükleri pozisyon, deneyim, kıdem ve şirket ölçeğine
                    göre değişir; özellikle küçük örneklemli segmentlerde
                    medyan değer katılımcı sayısıyla birlikte okunmalıdır.
                </p>
                <p>
                    Maaşlar yalnızca bilgilendirme ve analiz amacıyla sunulur;
                    garanti edilmiş piyasa ücreti, istihdam veya finans
                    tavsiyesi ya da ücret taahhüdü değildir. Olası işleme veya
                    yorumlama hatalarını{' '}
                    <a href={`mailto:${PROJECT_LINKS.email}`}>
                        {PROJECT_LINKS.email}
                    </a>{' '}
                    adresine iletebilirsiniz.
                </p>
            </aside>
        </div>
    </section>
);

export default AboutProject;
